import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartKeys } from '@config/keys/cart.keys';
import {
  RecommendationItem,
  RecommendationsApiService,
  RecommendationsResponse,
} from '@core/services/api/recommendations/recommendations-api.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { isSameCartContent } from '@shared/utils/cart/cart-equality.util';

import {
  CART_RECOMMENDATION_CARDS_BLOCK_EMPTY_VM,
  CartRecommendationCardsBlockDataSource,
  CartRecommendationCardsBlockViewModel,
} from './cart-recommendation-cards-block-view-model';

@Injectable()
export class CartRecommendationCardsBlockStoreDataSource implements CartRecommendationCardsBlockDataSource {
  private readonly recommendationsApiService = inject(RecommendationsApiService);
  private readonly cartSelector = inject(CartStoreSelector);
  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly ordersService = inject(OrdersService);

  readonly vm$: Observable<CartRecommendationCardsBlockViewModel> = combineLatest([
    // Same guard as suggestions sets: a new cart object with the same content must not
    // cancel the in-flight request via switchMap.
    this.cartSelector.selectActive$.pipe(distinctUntilChanged(isSameCartContent)),
    // Shop entity is patched often; only the id should retrigger recommendations.
    this.shopSelector.selectActive$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)),
  ]).pipe(
    switchMap(([cart, shop]) => {
      if (!cart || !shop) {
        return of(CART_RECOMMENDATION_CARDS_BLOCK_EMPTY_VM);
      }
      const marketplaceId = cart[CartKeys.Marketplace];
      const shopId = cart[CartKeys.Shop] ?? shop.id;
      const order = this.ordersService.mapCartToOrderValues(cart, []);
      if (!order || !marketplaceId || !shopId) {
        return of(CART_RECOMMENDATION_CARDS_BLOCK_EMPTY_VM);
      }
      return this.recommendationsApiService
        .getRecommendations$({
          order,
          marketplace: marketplaceId,
          shop: typeof shopId === 'string' ? shopId : shop.id,
        })
        .pipe(
          map((res: RecommendationsResponse) => ({
            recommendations: (res?.recommendations ?? []).filter(
              (item): item is RecommendationItem => item?.type === 'product',
            ),
          })),
          catchError(() => of(CART_RECOMMENDATION_CARDS_BLOCK_EMPTY_VM)),
        );
    }),
  );
}
